import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import {
  ACCEPT_TERMS_OF_SERVICE_MUTATION,
  LOGOUT_MUTATION,
} from '../app/graphql/authQueries';
import { loginUser, prepareReleaseNotes } from '../app/redux/auth';
import routes from '../app/routes';
import { useFlashMessageContext } from '../components/dialogs/FlashMessageProvider';
import LoginForm from '../forms/LoginForm';
import NonLoggedPage from '../templates/NonLoggedPage';
import { useNonLoggedPageStyles } from '../templates/NonLoggedPage.styles';
import TermsOfServicePage from './TermsOfServicePage';

export default function LoginPage() {
  const { styles } = useNonLoggedPageStyles();
  const location = useLocation();
  const [termsOfService, setTermsOfService] = useState();
  const [loginResult, setLoginResult] = useState();
  const onTermsOfService = useCallback(({ termsOfService: tos, ...lr }) => {
    setLoginResult(lr);
    setTermsOfService(tos);
  }, []);

  const redirect = location.state?.from?.pathname || routes.root;

  const dispatch = useDispatch();
  const { push } = useHistory();
  const onLoginSuccess = useCallback(
    ({ email, token, releaseNotes }) => {
      dispatch(loginUser(email, token));
      if (releaseNotes) {
        dispatch(prepareReleaseNotes(releaseNotes));
      }
      push(redirect);
    },
    [dispatch, push, redirect]
  );

  const { errorMessage } = useFlashMessageContext();
  const [acceptTos] = useMutation(ACCEPT_TERMS_OF_SERVICE_MUTATION, {
    context: { headers: { 'auth-token': loginResult?.token } },
  });
  const [logout] = useMutation(LOGOUT_MUTATION, {
    context: { headers: { 'auth-token': loginResult?.token } },
  });
  const onTosAccept = useCallback(async () => {
    try {
      await acceptTos();
      // These will cause unmounting, so we want to wait until all operations are completed
      window.requestAnimationFrame(() => {
        onLoginSuccess(loginResult);
      });
    } catch (e) {
      errorMessage(e);
    }
  }, [acceptTos, errorMessage, loginResult, onLoginSuccess]);

  const onTosCancel = useCallback(async () => {
    try {
      await logout();
      setTermsOfService(undefined);
      setLoginResult(undefined);
    } catch (e) {
      errorMessage(e);
    }
  }, [errorMessage, logout]);

  if (termsOfService) {
    return (
      <TermsOfServicePage
        content={termsOfService}
        onAccept={onTosAccept}
        onCancel={onTosCancel}
      />
    );
  }

  return (
    <NonLoggedPage id="LoginPage">
      <div className={classNames(styles.columns, styles.loginPageColumns)}>
        <LoginForm
          onLoginSuccess={onLoginSuccess}
          onTermsOfService={onTermsOfService}
        />
      </div>
    </NonLoggedPage>
  );
}
